<script lang="ts" setup>
import { ArrowTopRightOnSquareIcon, StarIcon as StarOutlineIcon } from "@heroicons/vue/24/outline";
import { ServerStackIcon, StarIcon as StarSolidIcon } from "@heroicons/vue/24/solid";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { twMerge } from "tailwind-merge";
import TableRow from "./ui/table/TableRow.vue";
import TableCell from "./ui/table/TableCell.vue";
import ACLClickToGrantButton from "./ACLClickToGrantButton.vue";
import ACLStatus from "./ACLStatus.vue";
import GrantCredentialsDialog, { type GrantCredentials, type ACLRequirements } from "./GrantCredentialsDialog.vue";
import { DateTime } from "luxon";
import { useAuthStore } from "../pinia/auth.ts";
import Tooltip from "./ui/Tooltip.vue";
import { getAclStatus } from "../shared/services/aclStatus";

const props = defineProps({
  mode: {
    type: String,
    required: true,
  },
  acl: {
    type: Object,
    required: true,
  },
  childAcl: {
    type: Object,
    required: false,
  },
  onGrantAcl: {
    type: Function,
    required: false,
  },
  onRevokeAcl: {
    type: Function,
    required: false,
  },
  onIncreaseAclUseCount: {
    type: Function,
    required: false,
  },
  onFavouriteAcl: {
    type: Function,
    required: false,
  },
  isFavourite: {
    type: Boolean,
    required: false,
    default: false,
  },
  dataSwapyItem: {
    type: String,
    required: false,
  },
});

onMounted(async () => {
  if (props.mode !== "card" && props.mode !== "row") {
    throw new Error("invalid mode for ACL componenet");
  }
});

const { t } = useI18n();
const router = useRouter();

const authStore = useAuthStore();
const theme = computed(() => authStore.theme?.Accent);
const highContrast = computed(() => authStore.theme?.HighAccessibility);

const status = computed(() => getAclStatus(props.acl.Status));
const childStatus = computed(() => (props.childAcl ? getAclStatus(props.childAcl.Status) : null));

const user = computed(() => authStore.user);
const userHasTotpEnabled = computed(() => user.value?.TotpEnabled && user.value?.UserVerified);

// Disable ACL if TOTP is required but user doesn't have TOTP set up
const disabledDueToTOTP = computed(() => props.acl.RequireTOTP && !userHasTotpEnabled.value);

const disabled = computed(
  () =>
    props.acl.DisableForUser || status.value.agentOffline || childStatus.value?.agentOffline || disabledDueToTOTP.value,
);
const url = computed(() => props.acl.URL);
const displayUrl = computed(() => getDisplayRoute(props.acl.URL));

const expiresAfter = ref(DateTime.fromISO(props.expiredAfter, { zone: "utc" }));
const expiresDiff = computed(() => (expiresAfter.value ? expiresAfter.value.diffNow("seconds").seconds : null));

// Grant credentials dialog state
const showCredentialsDialog = ref(false);
const pendingGrantEvent = ref<Event | null>(null);
const credentialsDialogLoading = ref(false);
const credentialsDialogError = ref<string | null>(null);

const aclRequirements = computed<ACLRequirements>(() => ({
  requireTicketNumber: props.acl.RequireTicketNumber ?? false,
  requireTOTP: props.acl.RequireTOTP ?? false,
  description: props.acl.Description ?? "",
}));

const needsCredentials = computed(() => aclRequirements.value.requireTicketNumber || aclRequirements.value.requireTOTP);

function handleGrantClick(e: Event) {
  if (needsCredentials.value) {
    pendingGrantEvent.value = e;
    credentialsDialogError.value = null;
    credentialsDialogLoading.value = false;
    showCredentialsDialog.value = true;
  } else {
    props.onGrantAcl?.(e);
  }
}

async function handleCredentialsSubmit(credentials: GrantCredentials) {
  // Store credentials on the ACL object for the grant handler to use
  if (credentials.ticketNumber) {
    props.acl.TicketNumber = credentials.ticketNumber;
  }
  if (credentials.totpCode) {
    props.acl.TOTPCode = credentials.totpCode;
  }

  credentialsDialogError.value = null;
  credentialsDialogLoading.value = true;

  try {
    if (pendingGrantEvent.value) {
      await props.onGrantAcl?.(pendingGrantEvent.value);
      pendingGrantEvent.value = null;
    }
    // Success - close the dialog
    showCredentialsDialog.value = false;
    credentialsDialogLoading.value = false;
  } catch (error: any) {
    // Keep dialog open and show error
    credentialsDialogLoading.value = false;
    // Server returns { Err: "err.grantTotpInvalid" } format - use it as i18n key
    const errKey = error?.response?.data?.Err;
    if (errKey && errKey.startsWith("err.")) {
      credentialsDialogError.value = t(errKey);
    } else {
      credentialsDialogError.value = error?.message || t("err.grantFailed");
    }
  }
}

function handleCredentialsClose() {
  showCredentialsDialog.value = false;
  pendingGrantEvent.value = null;
  credentialsDialogError.value = null;
  credentialsDialogLoading.value = false;
}

let interval;

onMounted(() => {
  interval = setInterval(async () => {
    if (expiresDiff.value !== null && expiresDiff.value < 0) {
      await authStore.fetchStatus({ force: true });
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval);
  }
});

function getDisplayRoute(url: string): string {
  try {
    const parsedUrl = new URL(url);
    const port = parsedUrl.port ? `:${parsedUrl.port}` : "";
    return `${parsedUrl.protocol}//${parsedUrl.hostname}${port}`;
  } catch {
    return url;
  }
}

function navigateToTotpSetup() {
  router.push({ name: "TwoFactorSetup" });
}
</script>

<template>
  <template v-if="mode === 'card'">
    <div
      :class="
        twMerge(
          'flex h-full min-h-[134px] flex-col justify-between space-y-4 rounded-md border bg-muted/30 px-4 py-4 dark:border-white/20',
          disabled && !disabledDueToTOTP ? 'pointer-events-none cursor-not-allowed opacity-50' : null,
          disabledDueToTOTP ? 'cursor-default' : null,
        )
      "
      :data-swapy-item="dataSwapyItem"
    >
      <div :class="['flex justify-between', disabledDueToTOTP ? 'opacity-50' : '']">
        <div class="flex flex-col overflow-hidden">
          <component
            :is="disabled || !Boolean(url) ? 'div' : 'a'"
            :aria-label="
              disabled || !Boolean(url) ? `ACL for ${acl.Description}` : `Open '${acl.Description}' ${url} in a new tab`
            "
            :class="{ 'pointer-events-none': disabled || !Boolean(url) }"
            v-bind="
              disabled || !Boolean(url)
                ? {}
                : {
                    href: url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
            "
            @click="!disabled && Boolean(url) && onIncreaseAclUseCount?.()"
            data-swapy-no-drag
          >
            <div class="flex items-center space-x-1">
              <font-awesome-icon :icon="acl.Icon" fixed-width />

              <h3 :title="acl.Description" aria-hidden="true" class="mr-2 truncate font-medium">
                {{ acl.Description }}
              </h3>
            </div>

            <span v-if="Boolean(url)" class="inline-flex max-w-xs items-center truncate text-sm text-muted-foreground">
              <ArrowTopRightOnSquareIcon class="mr-1 h-3 w-3 flex-shrink-0" />
              <span class="truncate">{{ getDisplayRoute(url) }}</span>
            </span>
          </component>
        </div>

        <div>
          <div class="flex items-center space-x-4">
            <Tooltip
              v-if="acl.GrantTypeFixedIps"
              :focusable="acl.GrantTypeFixedIps"
              :aria-label="`'${acl.Description}' grants access to a pre-defined set of IP addresses, not your specific IP address.`"
              text="This ACL grants access to a pre-defined set of IP addresses, not your specific IP address."
            >
              <span class="flex items-center">
                <ServerStackIcon class="h-4 w-4" data-swapy-no-drag />
              </span>
            </Tooltip>

            <component
              :is="disabled ? 'div' : 'button'"
              v-if="isFavourite"
              v-bind="disabled ? {} : { 'aria-label': 'Remove from favourites' }"
              @click="disabled ? undefined : onFavouriteAcl?.($event)"
            >
              <StarSolidIcon
                :class="['h-4 w-4', disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
                data-swapy-no-drag
              />
            </component>
            <component
              :is="disabled ? 'div' : 'button'"
              v-bind="disabled ? {} : { 'aria-label': 'Add to favourites' }"
              v-else
              @click="disabled ? undefined : onFavouriteAcl?.($event)"
            >
              <StarOutlineIcon
                :class="['h-4 w-4', disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
                data-swapy-no-drag
              />
            </component>
          </div>
        </div>
      </div>
      <div class="grid min-h-8 grid-cols-2 items-center">
        <template v-if="disabledDueToTOTP">
          <div class="col-span-2 flex items-center justify-between">
            <div class="flex flex-col">
              <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.totpRequired") }}</span>
              <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.setupTotpFirst") }}</span>
            </div>
            <button
              type="button"
              class="pointer-events-auto rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              @click="navigateToTotpSetup"
            >
              {{ t("fields.acls.setupTotp") }}
            </button>
          </div>
        </template>
        <template v-else-if="acl.DisableForUser">
          <div class="flex flex-col" :class="{ 'col-span-2': acl.DisableForUser }">
            <span class="text-sm font-medium text-muted-foreground">{{ t("common.disabled") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.consultYourAdmin") }}</span>
          </div>
        </template>
        <template v-else-if="status.agentOffline">
          <div class="col-span-2 flex flex-col">
            <span class="text-sm font-medium text-muted-foreground">Agent offline</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.consultYourAdmin") }}</span>
          </div>
        </template>
        <template v-else-if="!acl.ClickToGrant && status.pending">
          <div class="flex flex-col">
            <span class="text-sm font-medium text-muted-foreground">Pending</span>
          </div>
        </template>
        <template v-else-if="acl.ClickToGrant && !(status.error || childStatus?.error)">
          <div>
            <ACLClickToGrantButton
              :key="`${acl.ACLID}-${acl.Status}`"
              :expired-after="acl.ExpiredAfter"
              :status="acl.Status"
              :on-grant-acl="handleGrantClick"
              :on-revoke-acl="onRevokeAcl"
            />
          </div>
        </template>
        <template v-else>
          <div role="presentation"><!-- This is a hack for the grid-cols-2 --></div>
        </template>

        <template
          v-if="!disabled && (status.grantIssued || childStatus?.grantIssued || status.error || childStatus?.error)"
        >
          <ACLStatus
            :key="`${acl.ACLID}-${acl.Status}`"
            :status="childStatus?.error ? childAcl.Status : acl.Status"
            :error="childStatus?.error ? childAcl.Error : acl.Error"
            :expired-after="acl.ExpiredAfter"
            :click-to-grant="acl.ClickToGrant"
            class="flex justify-end"
          />
        </template>
      </div>
    </div>
  </template>
  <template v-if="mode === 'row'">
    <TableRow
      :class="{
        'cursor-not-allowed opacity-50': disabled && !disabledDueToTOTP,
        'pointer-events-none': disabled && !disabledDueToTOTP,
      }"
    >
      <TableCell>
        <div class="flex items-center space-x-4">
          <div>
            <StarSolidIcon
              v-if="isFavourite && !disabled"
              class="h-4 w-4 cursor-pointer"
              @click="!disabled && onFavouriteAcl?.($event)"
              data-swapy-no-drag
            />
            <StarOutlineIcon
              v-else-if="!disabled"
              class="h-4 w-4 cursor-pointer"
              @click="!disabled && onFavouriteAcl?.($event)"
              data-swapy-no-drag
            />
            <StarSolidIcon v-else-if="isFavourite && disabled" class="h-4 w-4 text-gray-400" data-swapy-no-drag />
            <StarOutlineIcon v-else class="h-4 w-4 text-gray-400" data-swapy-no-drag />
          </div>

          <Tooltip
            v-if="acl.GrantTypeFixedIps"
            :focusable="acl.GrantTypeFixedIps"
            :aria-label="`'${acl.Description}' grants access to a pre-defined set of IP addresses, not your specific IP address.`"
            text="This ACL grants access to a pre-defined set of IP addresses, not your specific IP address."
          >
            <span class="flex items-center">
              <ServerStackIcon class="h-4 w-4" data-swapy-no-drag />
            </span>
          </Tooltip>
        </div>
      </TableCell>

      <TableCell class="truncate" colspan="2">
        <template v-if="disabled">
          <div :class="disabledDueToTOTP ? 'text-gray-500' : 'text-gray-500'">
            <div :class="disabledDueToTOTP ? 'opacity-50' : ''">{{ acl.Description }}</div>
            <div
              v-if="Boolean(url)"
              :class="[
                'mt-1 text-sm text-muted-foreground md:ml-3 md:mt-0 md:inline',
                disabledDueToTOTP ? 'opacity-50' : '',
              ]"
            >
              <span class="hidden md:inline">&nbsp;&nbsp;&nbsp;</span>{{ displayUrl }}
            </div>
            <div class="mt-1 text-sm italic">
              <template v-if="disabledDueToTOTP">
                <span class="opacity-50">{{ t("fields.acls.totpRequired") }}</span>
                <span class="opacity-50"> - </span>
                <span class="opacity-50">{{ t("fields.acls.setupTotpFirst") }}</span>
                <button
                  type="button"
                  class="ml-2 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground opacity-100 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  @click="navigateToTotpSetup"
                >
                  {{ t("fields.acls.setupTotp") }}
                </button>
              </template>
              <template v-else-if="acl.DisableForUser">
                <span>{{ t("common.disabled") }}</span>
                <span> - </span>
                <span>{{ t("fields.acls.consultYourAdmin") }}</span>
              </template>
              <template v-else>
                <span>Agent offline</span>
                <span> - </span>
                <span>{{ t("fields.acls.consultYourAdmin") }}</span>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <a v-if="Boolean(url)" :href="url" class="block md:inline" target="_blank">
            <div class="md:inline">{{ acl.Description }}</div>
            <div class="mt-1 text-sm text-muted-foreground md:ml-3 md:mt-0 md:inline">
              <span class="hidden md:inline">&nbsp;&nbsp;&nbsp;</span>{{ displayUrl }}
            </div>
          </a>
          <div v-else>
            <span>{{ acl.Description }}</span>
          </div>
        </template>
      </TableCell>

      <!-- Only show access controls if not disabled -->
      <template v-if="!disabled">
        <template v-if="acl.ClickToGrant && status.grantReady && !(status.error || childStatus?.error)">
          <TableCell colspan="2" class="py-2 text-center">
            <ACLClickToGrantButton
              :key="`${acl.ACLID}-${acl.Status}`"
              :expired-after="acl.ExpiredAfter"
              :status="acl.Status"
              :on-grant-acl="handleGrantClick"
              :on-revoke-acl="onRevokeAcl"
            />
          </TableCell>
        </template>
        <template
          v-else-if="
            acl.ClickToGrant &&
            (status.grantIssued || childStatus?.grantIssued) &&
            !(status.error || childStatus?.error)
          "
        >
          <!-- Desktop: Button and Status in separate columns -->
          <TableCell class="hidden py-2 text-right md:table-cell" colspan="1">
            <ACLClickToGrantButton
              :key="`${acl.ACLID}-${acl.Status}`"
              :expired-after="acl.ExpiredAfter"
              :status="acl.Status"
              :on-grant-acl="handleGrantClick"
              :on-revoke-acl="onRevokeAcl"
            />
          </TableCell>
          <TableCell class="hidden py-2 text-left md:table-cell" colspan="1">
            <ACLStatus
              :key="`${acl.ACLID}-${acl.Status}`"
              :status="childStatus?.error ? childAcl.Status : acl.Status"
              :error="childStatus?.error ? childAcl.Error : acl.Error"
              :expired-after="acl.ExpiredAfter"
              :click-to-grant="acl.ClickToGrant"
            />
          </TableCell>

          <!-- Mobile: Combined column with Status above Button -->
          <TableCell class="py-2 text-center md:hidden" colspan="2">
            <div class="flex flex-col items-center space-y-2">
              <ACLStatus
                :key="`${acl.ACLID}-${acl.Status}`"
                :status="childStatus?.error ? childAcl.Status : acl.Status"
                :error="childStatus?.error ? childAcl.Error : acl.Error"
                :expired-after="acl.ExpiredAfter"
                :click-to-grant="acl.ClickToGrant"
              />
              <ACLClickToGrantButton
                :key="`${acl.ACLID}-${acl.Status}`"
                :expired-after="acl.ExpiredAfter"
                :status="acl.Status"
                :on-grant-acl="handleGrantClick"
                :on-revoke-acl="onRevokeAcl"
              />
            </div>
          </TableCell>
        </template>
        <template v-else>
          <TableCell class="py-2 text-center" colspan="2">
            <ACLStatus
              :key="`${acl.ACLID}-${acl.Status}`"
              :status="childStatus?.error ? childAcl.Status : acl.Status"
              :error="childStatus?.error ? childAcl.Error : acl.Error"
              :expired-after="acl.ExpiredAfter"
              :click-to-grant="acl.ClickToGrant"
            />
          </TableCell>
        </template>
      </template>

      <!-- Show disabled message for access columns when disabled -->
      <template v-else>
        <TableCell class="py-2 text-center text-gray-400" colspan="2">
          <span class="italic">Access restricted</span>
        </TableCell>
      </template>
    </TableRow>
  </template>

  <!-- Grant Credentials Dialog -->
  <GrantCredentialsDialog
    :show="showCredentialsDialog"
    :acl="aclRequirements"
    :error="credentialsDialogError"
    :is-loading="credentialsDialogLoading"
    @close="handleCredentialsClose"
    @submit="handleCredentialsSubmit"
  />
</template>
